// src/pages/HomePage.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Code2, Database, Layout, Server, ExternalLink, Mail, Phone, MapPin } from 'lucide-react';
import Typewriter from 'typewriter-effect';
import { Link } from 'react-router-dom';
import SEO from '../component/SEO';
import { homeFaqSchema } from '../component/SEO';
import { SITE } from '../config/seo';
import vanshpatel from '../image/vanshpatel.png';
import Feedbackfrom from './Feedbackform';

const API       = 'https://profolionode.vanshpatel.in/api/projects';
const CACHE_KEY = 'home_featured_projects';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ── Parse tech field (string / JSON / array) ──────────────────────────────
const parseTech = (techValue) => {
  if (!techValue) return [];
  if (Array.isArray(techValue)) return techValue;
  try {
    const parsed = JSON.parse(techValue);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return techValue.replace(/[{}"]/g, '').split(',').map((t) => t.trim()).filter(Boolean);
};

// ── Skeleton card ─────────────────────────────────────────────────────────
const SkeletonProjectCard = () => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-lg animate-pulse">
    <div className="h-52 bg-slate-200 dark:bg-slate-700" />
    <div className="p-6 space-y-3">
      <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-4/5" />
      <div className="flex gap-2 pt-2">
        <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
      </div>
    </div>
  </div>
);

const HomePage = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects]   = useState(true);

  // ── Fetch top 3 projects with cache ──────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();

    const fetchProjects = async () => {
      // 1️⃣ Instant load from cache
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setFeaturedProjects(data);
            setLoadingProjects(false);
            return; // no API call needed
          }
        }
      } catch (_) {}

      // 2️⃣ Fetch from API
      try {
        const res  = await fetch(API, { signal: controller.signal });
        const json = await res.json();

        const items = (json.data || json || []).slice(0, 3).map((p) => ({
          ...p,
          techStack: parseTech(p.tech),
          live: p.link || '#',
        }));

        setFeaturedProjects(items);

        // Save to cache
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: items, timestamp: Date.now() }));
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Failed to fetch projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
    return () => controller.abort();
  }, []);

  // ── JSON-LD ───────────────────────────────────────────────────────────────
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: `${SITE.name} – Full Stack Developer Portfolio`,
    url: SITE.url,
    description: 'Portfolio of Vansh Patel, Full Stack Developer specialising in React.js, Node.js, and JavaScript.',
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE.url}/portfolio?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Full Stack Development Services',
    provider: { '@type': 'Person', name: SITE.name, url: SITE.url },
    serviceType: ['React.js Development', 'Node.js & Express API Development', 'Full Stack Web Applications', 'REST API Development', 'Database Design & Development'],
    areaServed: 'Worldwide',
    description: 'Professional full stack development services including React.js frontend, Node.js backend, REST APIs, and database design.',
  };

  return (
    <>
      <SEO page="home" schema={[websiteSchema, serviceSchema, homeFaqSchema]} />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="min-h-[calc(100vh-4rem)] flex items-center justify-center relative overflow-hidden"
        aria-label="Hero Section"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 animate-gradient bg-[length:400%_400%]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 px-4 py-2 rounded-full mb-6"
            >
              <Code2 size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Available for work</span>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6">
              Hi, I'm <span className="gradient-text">Vansh Patel</span>
            </h1>

            <div className="text-2xl sm:text-3xl md:text-4xl text-slate-600 dark:text-slate-300 mb-8">
              <Typewriter
                options={{
                  strings: ['Full Stack Developer', 'React.js Developer', 'Node.js Developer', 'JavaScript Expert', 'Problem Solver'],
                  autoStart: true,
                  loop: true,
                  delay: 50,
                  deleteSpeed: 30,
                }}
              />
            </div>

            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-12">
              I craft beautiful, responsive, and high-performance web applications using{' '}
              <strong className="text-blue-600">React.js</strong>,{' '}
              <strong className="text-blue-600">Node.js</strong>, and{' '}
              <strong className="text-blue-600">modern JavaScript</strong>.
              Based in Ahmedabad, available worldwide.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/portfolio">
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  View My Work
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all border border-slate-200 dark:border-slate-700"
                >
                  Let's Connect
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        >
          <ArrowDown className="text-slate-400" size={32} />
        </motion.div>
      </section>

      {/* ── About Me Preview ─────────────────────────────────────────────── */}
      <section className="py-20 bg-white dark:bg-slate-900" aria-label="About Me Preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={vanshpatel}
                  alt="Vansh Patel - Full Stack Developer from Ahmedabad"
                  title="Vansh Patel – Full Stack Developer"
                  className="w-full h-auto"
                  loading="lazy"
                  width="600"
                  height="600"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-2">{SITE.name}</h2>
              <p className="text-xl text-blue-600 dark:text-blue-400 mb-6">
                Full Stack Developer | React.js &amp; Node.js Specialist
              </p>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                Passionate Full Stack Developer specialising in{' '}
                <strong className="text-blue-600">React.js</strong>,{' '}
                <strong className="text-blue-600">Node.js</strong>, and{' '}
                <strong className="text-blue-600">modern JavaScript</strong>.
                I build responsive, high-performance web applications and REST APIs,
                delivering seamless user experiences. Currently working at{' '}
                <strong className="text-blue-600">Purvsoft Technologies</strong>,
                Ahmedabad. BCA graduate from Silver Oak University with CGPA 8.50.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  { icon: Mail,   href: `mailto:${SITE.email}`,        label: SITE.email },
                  { icon: Phone,  href: `tel:${SITE.phone}`,           label: SITE.phone },
                  { icon: MapPin, href: null,                           label: SITE.location },
                ].map(({ icon: Icon, href, label }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                    {href ? (
                      <a href={href} className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 transition-colors">
                        <Icon size={15} className="text-blue-600" />{label}
                      </a>
                    ) : (
                      <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <Icon size={15} className="text-blue-600" />{label}
                      </span>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 transition-colors">
                    <span>🔗</span> linkedin.com/in/patelvansh13
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  <a href={SITE.social.github} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 transition-colors">
                    <span>💻</span> github.com/Vansh13042005
                  </a>
                </div>
              </div>

              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Know More About Me
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800" aria-label="Services Section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }} viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">What I Do</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full" />
            <p className="text-slate-600 dark:text-slate-400 mt-4">Professional full stack development services</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Layout,   title: 'React.js Development',  desc: 'Responsive, modern UI components and single-page applications' },
              { icon: Server,   title: 'Node.js & Express',      desc: 'Scalable REST APIs and backend services' },
              { icon: Database, title: 'Database Design',        desc: 'Efficient MySQL & MongoDB architecture' },
              { icon: Code2,    title: 'Full Stack Projects',    desc: 'End-to-end web applications built for performance' },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }} viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="bg-white dark:bg-slate-900 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all"
              >
                <service.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Projects ─────────────────────────────────────────────── */}
      <section className="py-20" aria-label="Featured Projects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }} viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">Featured Projects</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full" />
            <p className="text-slate-600 dark:text-slate-400 mt-4">Some of my best work</p>
          </motion.div>

          {/* Skeleton */}
          {loadingProjects && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => <SkeletonProjectCard key={i} />)}
            </div>
          )}

          {/* Cards */}
          {!loadingProjects && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6 }}
                  className="bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden group">
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={`${project.title} – project by Vansh Patel`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center">
                        <span className="text-5xl opacity-30">🖥️</span>
                      </div>
                    )}
                    {project.category && (
                      <span className="absolute top-3 right-3 px-2 py-1 text-xs bg-blue-600 text-white rounded font-medium">
                        {project.category}
                      </span>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">{project.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm line-clamp-2">{project.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.techStack.slice(0, 3).map((tech, i) => (
                        <span key={i} className="px-3 py-1 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full border border-slate-200 dark:border-slate-600">
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center gap-6">
                      {project.github ? (
                        <a href={project.github} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          View Code
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-slate-300 dark:text-slate-600 cursor-not-allowed">View Code</span>
                      )}

                      {project.live && project.live !== '#' ? (
                        <a href={project.live} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          <ExternalLink size={15} /> Live Demo
                        </a>
                      ) : (
                        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-300 dark:text-slate-600 cursor-not-allowed">
                          <ExternalLink size={15} /> Live Demo
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all"
              >
                View All Projects
              </motion.button>
            </Link>
          </div>
        </div>
      </section>
      <Feedbackfrom />
    </>
  );
};

export default HomePage;